import { useCallback, useEffect, useState } from "react";
import { checkApiHealth, type ApiHealth } from "@/services/healthService";

type BadgeState = "checking" | "live" | "demo" | "down";

const COPY: Record<BadgeState, { label: string; icon: string; tone: string; hint: string }> = {
  checking: {
    label: "Checking API…",
    icon: "ri-loader-4-line animate-spin",
    tone: "border-background-300 bg-background-100 text-foreground-600",
    hint: "Pinging the REST API health endpoint.",
  },
  live: {
    label: "Live API · DB up",
    icon: "ri-database-2-line",
    tone: "border-accent-300 bg-accent-100 text-accent-900",
    hint: "Connected to the Node/MariaDB backend — showing real data.",
  },
  demo: {
    label: "Demo data",
    icon: "ri-flask-line",
    tone: "border-secondary-200 bg-secondary-100 text-secondary-900",
    hint: "No API URL configured — the site is running on local mock data.",
  },
  down: {
    label: "API unreachable",
    icon: "ri-plug-line",
    tone: "border-background-300 bg-background-200 text-foreground-700",
    hint: "An API URL is set but the backend did not respond — pages show an error with a retry button.",
  },
};

/**
 * Small status pill that proves, at a glance, whether the page is talking to
 * the live backend or rendering demo data. Click to re-check.
 */
export default function ApiStatusBadge() {
  const [health, setHealth] = useState<ApiHealth | null>(null);
  const [checking, setChecking] = useState(true);

  const run = useCallback(async () => {
    setChecking(true);
    const result = await checkApiHealth();
    setHealth(result);
    setChecking(false);
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  const state: BadgeState = checking
    ? "checking"
    : !health?.configured
      ? "demo"
      : health.live
        ? "live"
        : "down";

  const { label, icon, tone, hint } = COPY[state];
  const version = state === "live" && health?.version ? ` · v${health.version}` : "";

  return (
    <button
      type="button"
      onClick={run}
      title={`${hint} Click to re-check.`}
      className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-opacity hover:opacity-80 ${tone}`}
    >
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <i className={`${icon} text-sm`} />
      </span>
      <span>
        {label}
        {version}
      </span>
    </button>
  );
}